import React, { useState, useEffect } from 'react';
import { useLocation, useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { IoIosRemoveCircle } from "react-icons/io";
const MySwal = withReactContent(Swal);
const CLINIC_ADDRESS_FOOTER = '123 Dental Street, Cityville, ST 54321 - Phone: (123) 456-7890 - Email: contact@hiradentallab.com';

// Reusing styles from InvoiceDetail for consistency
const styles = {
  pageContainer: {
    backgroundColor: '#f5f7fa',
    minHeight: '100vh',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  sidebar: {
    borderRight: '1px solid #e5e5e5',
    minHeight: 'calc(100vh - 70px)',
    padding: '20px 0',
    backgroundColor: '#ffffff',
  },
  sidebarTitle: {
    margin: '15px 0 20px 30px',
    color: '#004aad',
    fontSize: '22px',
    fontWeight: '600',
  },
  sidebarLink: {
    textDecoration: 'none',
    color: '#0056b3',
    fontWeight: '500',
    fontSize: '16px',
    display: 'block',
    margin: '0 30px 15px',
    padding: '8px 12px',
    borderRadius: '4px',
    transition: 'background-color 0.2s ease, color 0.2s ease',
  },
  contentWrapper: {
    padding: '30px',
  },
  contentBox: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    padding: '30px',
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '25px',
    paddingBottom: '15px',
    borderBottom: '2px solid #004aad',
  },
  invoiceHeading: {
    fontSize: '24px',
    color: '#262626',
    fontWeight: '600',
  },
  formSection: {
    marginBottom: '25px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#004aad',
    marginBottom: '15px',
    paddingBottom: '10px',
    borderBottom: '1px solid #e5e5e5',
  },
  formGroup: {
    marginBottom: '15px',
  },
  formLabel: {
    fontWeight: '600',
    color: '#444',
    fontSize: '13px',
    marginBottom: '5px',
    display: 'block',
  },
  formInput: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #e5e5e5',
    borderRadius: '4px',
    fontSize: '13px',
    color: '#333',
    outline: 'none',
    transition: 'border-color 0.2s ease',
  },
  formInputFocus: {
    borderColor: '#004aad',
    boxShadow: '0 0 4px rgba(0,74,173,0.2)',
  },
  itemsTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '12px',
    marginBottom: '20px',
  },
  tableHeader: {
    backgroundColor: '#eaf0ff',
    color: '#004aad',
    fontWeight: 'bold',
  },
  tableCell: {
    border: '1px solid #ddd',
    padding: '10px',
    textAlign: 'left',
  },
  textRight: {
    textAlign: 'right',
  },
  tableRowEven: {
    backgroundColor: '#f8f9fa',
  },
  noItemsRow: {
    textAlign: 'center',
    color: '#777',
    padding: '15px',
  },
  actionButton: {
    padding: '12px 24px',
    backgroundColor: '#1e40af',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '500',
    fontSize: '14px',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(30, 64, 175, 0.2)',
    transition: 'all 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  loadingError: {
    textAlign: 'center',
    marginTop: '60px',
    fontSize: '18px',
    padding: '20px',
    color: '#d9534f',
    backgroundColor: '#fff',
    borderRadius: '8px',
  },
  footerSection: {
    textAlign: 'center',
    marginTop: '40px',
    paddingTop: '20px',
    borderTop: '1px solid #ccc',
    fontSize: '11px',
    color: '#666',
  },
  thankYou: {
    color: '#4CAF50',
    fontStyle: 'italic',
    display: 'block',
    marginTop: '8px',
    fontSize: '12px',
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '16px',
    marginTop: '30px',
  },
  secondaryButton: {
    backgroundColor: '#ffffff',
    color: '#374151',
    border: '1px solid #d1d5db',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  },
};

// CSS for page transitions
const transitionStyles = `
  .fade-enter {
    opacity: 0;
  }
  .fade-enter-active {
    opacity: 1;
    transition: opacity 300ms ease-in;
  }
  .fade-exit {
    opacity: 1;
  }
  .fade-exit-active {
    opacity: 0;
    transition: opacity 300ms ease-out;
  }
`;

const EditInvoice = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { invoiceNumber } = useParams();
  const { invoiceData: initialInvoiceData } = location.state || {};

  const [formData, setFormData] = useState({
    invoiceNumber: '',
    invoiceDate: '',
    caseId: '',
    accountId: '',
    shipper: '',
    trackNumber: '',
    shippingCost: '',
    rescheduleOption: false,
    invoiceNow: false,
    relatedCase: '',
    addShippingCost: false,
    items: [],
    additionalLines: [],
    subtotal: '',
    taxes: '',
    total: '',
    invoiceMessage: '',
    printAfterSave: false,
    status: 'unpaid',
    paid: '',
    remaining: '',
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Calculate rate from amount and quantity
  const calculateRate = (quantity, amount) => {
    const qty = parseFloat(quantity) || 1;
    const amt = parseFloat(amount) || 0;
    return qty > 0 ? (amt / qty).toFixed(2) : '0.00';
  };

  // Process items data to include rate
  const processItemsData = (items) => {
    return (items || []).map(item => ({
      ...item,
      rate: item.rate || calculateRate(item.quantity, item.amount),
      quantity: String(item.quantity || '1'),
      amount: String(parseFloat(item.amount || 0).toFixed(2))
    }));
  };

  // Fetch invoice data
  useEffect(() => {
    const fetchInvoiceData = async () => {
      try {
        let invoiceData;

        if (initialInvoiceData && String(initialInvoiceData.invoiceNumber) === invoiceNumber) {
          invoiceData = initialInvoiceData;
        } else {
          const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/invoices/${invoiceNumber}`);
          if (response.data) {
            invoiceData = response.data;
          } else {
            throw new Error('Invoice not found');
          }
        }

        // Process items to include rate
        const processedItems = processItemsData(invoiceData.items);

        setFormData({
          ...invoiceData,
          invoiceDate: invoiceData.invoiceDate
            ? new Date(invoiceData.invoiceDate).toISOString().split('T')[0]
            : '',
          items: processedItems,
          additionalLines: invoiceData.additionalLines || [],
          shippingCost: String(invoiceData.shippingCost || ''),
          subtotal: String(parseFloat(invoiceData.subtotal || 0).toFixed(2)),
          taxes: String(parseFloat(invoiceData.taxes || 0).toFixed(2)),
          total: String(parseFloat(invoiceData.total || 0).toFixed(2)),
          paid: String(parseFloat(invoiceData.paid || 0).toFixed(2)),
          remaining: String((parseFloat(invoiceData.total || 0) - parseFloat(invoiceData.paid || 0)).toFixed(2)),
        });

        setLoading(false);
      } catch (err) {
        console.error('Error fetching invoice data:', err);
        setError('Failed to load invoice data: ' + (err.response?.data?.message || err.message));
        setLoading(false);
      }
    };

    if (invoiceNumber) {
      fetchInvoiceData();
    } else {
      setError('Invoice number is missing.');
      setLoading(false);
    }
  }, [invoiceNumber, initialInvoiceData]);

  // Inject transition styles
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.type = 'text/css';
    styleSheet.innerText = transitionStyles;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  // Calculate totals whenever items, additionalLines, taxes, or paid amount changes
  useEffect(() => {
    const calculateTotals = () => {
      const itemsTotal = (formData.items || []).reduce((sum, item) => {
        return sum + (parseFloat(item.amount) || 0);
      }, 0);

      const additionalLinesTotal = (formData.additionalLines || []).reduce((sum, line) => {
        return sum + (parseFloat(line.amount) || 0);
      }, 0);

      const subtotal = itemsTotal + additionalLinesTotal;
      const taxes = parseFloat(formData.taxes) || 0;
      const total = subtotal + taxes;
      const paid = parseFloat(formData.paid) || 0;
      const remaining = total - paid;

      setFormData(prev => ({
        ...prev,
        subtotal: subtotal.toFixed(2),
        total: total.toFixed(2),
        remaining: remaining.toFixed(2)
      }));
    };

    calculateTotals();
  }, [formData.items, formData.additionalLines, formData.taxes, formData.paid]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Handle item changes
  const handleItemChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedItems = [...prev.items];

      if (field === 'rate') {
        // When rate changes, update the amount based on quantity
        const quantity = parseFloat(updatedItems[index].quantity) || 0;
        const rate = parseFloat(value) || 0;
        updatedItems[index] = {
          ...updatedItems[index],
          rate: value,
          amount: (quantity * rate).toFixed(2)
        };
      } else if (field === 'quantity') {
        // When quantity changes, update the amount based on rate
        const quantity = parseFloat(value) || 0;
        const rate = parseFloat(updatedItems[index].rate) || 0;
        updatedItems[index] = {
          ...updatedItems[index],
          quantity: value,
          amount: (quantity * rate).toFixed(2)
        };
      } else {
        // For other fields (description), just update the field
        updatedItems[index] = { ...updatedItems[index], [field]: value };
      }

      return {
        ...prev,
        items: updatedItems
      };
    });
  };

  // Handle additional lines changes
  const handleAdditionalLineChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedLines = [...prev.additionalLines];
      updatedLines[index] = { ...updatedLines[index], [field]: value };
      return { ...prev, additionalLines: updatedLines };
    });
  };

  // Add new item
  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: '1', amount: '0.00', rate: '0.00' }],
    }));
  };

  // Add new additional line
  const addAdditionalLine = () => {
    setFormData((prev) => ({
      ...prev,
      additionalLines: [...prev.additionalLines, { description: '', amount: '0.00' }],
    }));
  };

  // Remove item
  const removeItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Remove additional line
  const removeAdditionalLine = (index) => {
    setFormData((prev) => ({
      ...prev,
      additionalLines: prev.additionalLines.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Prepare the data with proper type conversion
      const requestData = {
        caseId: String(formData.caseId || ''),
        accountId: String(formData.accountId || ''),
        trackNumber: formData.trackNumber || undefined,
        shippingCost: parseFloat(formData.shippingCost) || 0,
        shipper: formData.shipper || undefined,
        rescheduleOption: Boolean(formData.rescheduleOption),
        invoiceNow: Boolean(formData.invoiceNow),
        relatedCase: formData.relatedCase || undefined,
        addShippingCost: Boolean(formData.addShippingCost),
        items: (formData.items || []).map(item => ({
          description: String(item.description || ''),
          quantity: parseFloat(item.quantity) || 0,
          amount: parseFloat(item.amount) || 0,
          rate: parseFloat(item.rate) || 0
        })),
        additionalLines: (formData.additionalLines || []).map(line => ({
          description: String(line.description || ''),
          amount: parseFloat(line.amount) || 0
        })),
        subtotal: parseFloat(formData.subtotal) || 0,
        taxes: parseFloat(formData.taxes) || 0,
        total: parseFloat(formData.total) || 0,
        invoiceDate: formData.invoiceDate || undefined,
        invoiceMessage: formData.invoiceMessage || undefined,
        printAfterSave: Boolean(formData.printAfterSave),
        status: ['paid', 'unpaid', 'overdue'].includes(formData.status)
          ? formData.status
          : 'unpaid',
        paid: parseFloat(formData.paid) || 0,
        remaining: parseFloat(formData.remaining) || 0
      };

      // Clean up undefined values
      const cleanData = Object.fromEntries(
        Object.entries(requestData).filter(([_, v]) => v !== undefined)
      );

      const response = await axios.put(
        `https://hms.backend.hiragroupcloud.com/api/invoices/edit-invoices/${invoiceNumber}`,
        cleanData,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data?.success) {
        // Show SweetAlert2 success alert
        await MySwal.fire({
          title: 'Success!',
          text: 'Invoice updated successfully!',
          icon: 'success',
          confirmButtonText: 'View Invoice',
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: true,
          buttonsStyling: false,
          customClass: {
            popup: 'swal2-popup',
            title: 'swal2-title',
            content: 'swal2-content',
            confirmButton: 'swal2-confirm btn btn-primary',
          },
        });

        navigate(`/invoices/${invoiceNumber}`, {
          state: {
            invoiceData: response.data.data,
            successMessage: 'Invoice updated successfully!'
          }
        });
      } else {
        throw new Error(response.data?.message || 'Failed to update invoice');
      }
    } catch (err) {
      console.error('Error updating invoice:', err);
      setError('Failed to update invoice: ' + (err.response?.data?.message || err.message));
      // Scroll to top to show error
      window.scrollTo(0, 0);
    }
  };

  return (
    <>
      <TransitionGroup>
        <CSSTransition key={location.key} classNames="fade" timeout={300}>
          <div style={styles.pageContainer}>
            <div className="row g-0">
              <div className="col-md-2" style={styles.sidebar}>
                <h2 style={styles.sidebarTitle}>Finances</h2>
                <div>
                  <Link
                    to="/postings"
                    style={styles.sidebarLink}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#eaf0ff';
                      e.currentTarget.style.color = '#003a8c';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#0056b3';
                    }}>
                    Postings
                  </Link>
                  <Link
                    to="/Statements"
                    style={styles.sidebarLink}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#eaf0ff';
                      e.currentTarget.style.color = '#003a8c';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#0056b3';
                    }}>
                    Statements
                  </Link>
                </div>
              </div>
              <div className="col-md-10" style={styles.contentWrapper}>
                <div style={styles.contentBox}>
                  <div style={styles.headerContainer}>
                    <div style={styles.invoiceHeading}>
                      Edit Invoice #{formData.invoiceNumber || 'N/A'}
                    </div>
                  </div>

                  {error && (
                    <div style={styles.loadingError}>{error}</div>
                  )}

                  {loading ? (
                    <div style={styles.loadingError}>Loading...</div>
                  ) : (
                    <form onSubmit={handleSubmit}>
                      <div style={styles.formSection}>
                        <div style={styles.sectionTitle}>Invoice Details</div>
                        <div style={styles.formGroup}>
                          <label style={styles.formLabel}>Invoice Number</label>
                          <input
                            type="text"
                            name="invoiceNumber"
                            value={formData.invoiceNumber}
                            readOnly
                            style={{ ...styles.formInput, backgroundColor: '#f8f9fa' }}
                          />
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.formLabel}>Invoice Date</label>
                          <input
                            type="date"
                            name="invoiceDate"
                            value={formData.invoiceDate}
                            onChange={handleInputChange}
                            style={styles.formInput}
                            onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}
                            onBlur={(e) => Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' })}
                          />
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.formLabel}>Case ID</label>
                          <input
                            type="text"
                            name="caseId"
                            value={formData.caseId || ''}
                            onChange={handleInputChange}
                            style={styles.formInput}
                            onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}
                            onBlur={(e) => Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' })}
                          />
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.formLabel}>Account ID</label>
                          <input
                            type="text"
                            name="accountId"
                            value={formData.accountId || ''}
                            onChange={handleInputChange}
                            style={styles.formInput}
                            onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}
                            onBlur={(e) => Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' })}
                          />
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.formLabel}>Shipper</label>
                          <input
                            type="text"
                            name="shipper"
                            value={formData.shipper || ''}
                            onChange={handleInputChange}
                            style={styles.formInput}
                            onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}
                            onBlur={(e) => Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' })}
                          />
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.formLabel}>Tracking Number</label>
                          <input
                            type="text"
                            name="trackNumber"
                            value={formData.trackNumber || ''}
                            onChange={handleInputChange}
                            style={styles.formInput}
                            onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}
                            onBlur={(e) => Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' })}
                          />
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.formLabel}>Shipping Cost</label>
                          <input
                            type="number"
                            step="0.01"
                            name="shippingCost"
                            value={formData.shippingCost || ''}
                            onChange={handleInputChange}
                            style={{ ...styles.formInput, textAlign: 'right' }}
                            onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}
                            onBlur={(e) => Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' })}
                          />
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.formLabel}>Invoice Message</label>
                          <textarea
                            name="invoiceMessage"
                            value={formData.invoiceMessage || ''}
                            onChange={handleInputChange}
                            style={{ ...styles.formInput, minHeight: '80px' }}
                            onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}
                            onBlur={(e) => Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' })}
                          />
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.formLabel}>Status</label>
                          <select
                            name="status"
                            value={formData.status || 'unpaid'}
                            onChange={handleInputChange}
                            style={styles.formInput}
                            onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}
                            onBlur={(e) => Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' })}
                          >
                            <option value="unpaid">Unpaid</option>
                            <option value="paid">Paid</option>
                            <option value="overdue">Overdue</option>
                          </select>
                        </div>
                        <div style={styles.formGroup}>
                          <label style={{ ...styles.formLabel, display: 'flex', alignItems: 'center' }}>
                            <input
                              type="checkbox"
                              name="rescheduleOption"
                              checked={formData.rescheduleOption || false}
                              onChange={handleInputChange}
                              style={{ marginRight: '8px' }}
                            />
                            Reschedule Option
                          </label>
                        </div>
                        <div style={styles.formGroup}>
                          <label style={{ ...styles.formLabel, display: 'flex', alignItems: 'center' }}>
                            <input
                              type="checkbox"
                              name="invoiceNow"
                              checked={formData.invoiceNow || false}
                              onChange={handleInputChange}
                              style={{ marginRight: '8px' }}
                            />
                            Invoice Now
                          </label>
                        </div>
                        <div style={styles.formGroup}>
                          <label style={{ ...styles.formLabel, display: 'flex', alignItems: 'center' }}>
                            <input
                              type="checkbox"
                              name="addShippingCost"
                              checked={formData.addShippingCost || false}
                              onChange={handleInputChange}
                              style={{ marginRight: '8px' }}
                            />
                            Add Shipping Cost
                          </label>
                        </div>
                        <div style={styles.formGroup}>
                          <label style={{ ...styles.formLabel, display: 'flex', alignItems: 'center' }}>
                            <input
                              type="checkbox"
                              name="printAfterSave"
                              checked={formData.printAfterSave || false}
                              onChange={handleInputChange}
                              style={{ marginRight: '8px' }}
                            />
                            Print After Save
                          </label>
                        </div>
                      </div>

                      <div style={styles.formSection}>
                        <div style={styles.sectionTitle}>Items</div>
                        <table style={styles.itemsTable}>
                          <thead>
                            <tr style={styles.tableHeader}>
                              <th style={{ ...styles.tableCell, width: '38%' }}>Description</th>
                              <th style={{ ...styles.tableCell, ...styles.textRight, width: '12%' }}>Qty</th>
                              <th style={{ ...styles.tableCell, ...styles.textRight, width: '20%' }}>Rate</th>
                              <th style={{ ...styles.tableCell, ...styles.textRight, width: '20%' }}>Amount</th>
                              <th style={{ ...styles.tableCell, ...styles.textRight, width: '10%' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(formData.items || []).map((item, index) => (
                              <tr key={index} style={index % 2 === 0 ? {} : styles.tableRowEven}>
                                <td style={styles.tableCell}>
                                  <input
                                    type="text"
                                    value={item.description || ''}
                                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                    style={styles.formInput}
                                    onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}
                                    onBlur={(e) => Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' })}
                                  />
                                </td>
                                <td style={{ ...styles.tableCell, ...styles.textRight }}>
                                  <input
                                    type="number"
                                    step="1"
                                    min="1"
                                    value={item.quantity || '1'}
                                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                    style={{ ...styles.formInput, textAlign: 'right' }}
                                    onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}
                                    onBlur={(e) => Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' })}
                                  />
                                </td>
                                <td style={{ ...styles.tableCell, ...styles.textRight }}>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={item.rate || '0.00'}
                                    onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                                    style={{ ...styles.formInput, textAlign: 'right' }}
                                    onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}
                                    onBlur={(e) => Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' })}
                                  />
                                </td>
                                <td style={{ ...styles.tableCell, ...styles.textRight }}>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={item.amount || '0.00'}
                                    readOnly
                                    style={{ ...styles.formInput, textAlign: 'right', backgroundColor: '#f8f9fa' }}
                                  />
                                </td>
                                <td style={{ ...styles.tableCell, ...styles.textRight }}>
                                  <button
                                    type="button"
                                    style={{
                                      color: '#d9534f',
                                      backgroundColor: 'transparent',
                                      border: 'none',
                                      cursor: 'pointer',
                                      fontSize: '20px',
                                      padding: '8px',
                                      borderRadius: '50%',
                                      transition: 'all 0.3s ease',
                                      outline: 'none',
                                    }}
                                    onClick={() => removeItem(index)}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.color = '#c9302c';
                                      e.currentTarget.style.transform = 'scale(1.2)';
                                      e.currentTarget.style.textShadow = '0 0 8px rgba(217, 83, 79, 0.9)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.color = '#d9534f';
                                      e.currentTarget.style.transform = 'scale(1)';
                                      e.currentTarget.style.textShadow = 'none';
                                    }}
                                  >
                                    <IoIosRemoveCircle style={{ transition: 'all 0.3s ease' }} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {(!formData.items || formData.items.length === 0) && (
                              <tr style={styles.noItemsRow}>
                                <td colSpan="5" style={styles.tableCell}>
                                  No items in this invoice.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        <button
                          type="button"
                          style={styles.actionButton}
                          onClick={addItem}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#003a8c';
                            e.currentTarget.style.boxShadow = '0 3px 6px rgba(0,58,140,0.25)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#004aad';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,74,173,0.2)';
                          }}
                        >
                          Add Item
                        </button>
                      </div>

                      <div style={styles.formSection}>
                        <div style={styles.sectionTitle}>Additional Lines</div>
                        <table style={styles.itemsTable}>
                          <thead>
                            <tr style={styles.tableHeader}>
                              <th style={{ ...styles.tableCell, width: '70%' }}>Description</th>
                              <th style={{ ...styles.tableCell, ...styles.textRight, width: '20%' }}>Amount</th>
                              <th style={{ ...styles.tableCell, ...styles.textRight, width: '10%' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(formData.additionalLines || []).map((line, index) => (
                              <tr key={index} style={index % 2 === 0 ? {} : styles.tableRowEven}>
                                <td style={styles.tableCell}>
                                  <input
                                    type="text"
                                    value={line.description || ''}
                                    onChange={(e) => handleAdditionalLineChange(index, 'description', e.target.value)}
                                    style={styles.formInput}
                                    onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}
                                    onBlur={(e) => Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' })}
                                  />
                                </td>
                                <td style={{ ...styles.tableCell, ...styles.textRight }}>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={line.amount || '0.00'}
                                    onChange={(e) => handleAdditionalLineChange(index, 'amount', e.target.value)}
                                    style={{ ...styles.formInput, textAlign: 'right' }}
                                    onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}
                                    onBlur={(e) => Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' })}
                                  />
                                </td>
                                <td style={{ ...styles.tableCell, ...styles.textRight }}>
                                  <button
                                    type="button"
                                    style={{
                                      color: '#d9534f',
                                      backgroundColor: 'transparent',
                                      border: 'none',
                                      cursor: 'pointer',
                                      fontSize: '20px',
                                      padding: '8px',
                                      borderRadius: '50%',
                                      transition: 'all 0.3s ease',
                                      outline: 'none',
                                    }}
                                    onClick={() => removeAdditionalLine(index)}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.color = '#c9302c';
                                      e.currentTarget.style.transform = 'scale(1.2)';
                                      e.currentTarget.style.textShadow = '0 0 8px rgba(217, 83, 79, 0.9)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.color = '#d9534f';
                                      e.currentTarget.style.transform = 'scale(1)';
                                      e.currentTarget.style.textShadow = 'none';
                                    }}
                                  >
                                    <IoIosRemoveCircle style={{ transition: 'all 0.3s ease' }} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {(!formData.additionalLines || formData.additionalLines.length === 0) && (
                              <tr style={styles.noItemsRow}>
                                <td colSpan="3" style={styles.tableCell}>
                                  No additional lines.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        <button
                          type="button"
                          style={styles.actionButton}
                          onClick={addAdditionalLine}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#003a8c';
                            e.currentTarget.style.boxShadow = '0 3px 6px rgba(0,58,140,0.25)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#004aad';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,74,173,0.2)';
                          }}
                        >
                          Add Additional Line
                        </button>
                      </div>

                      <div style={styles.formSection}>
                        <div style={styles.sectionTitle}>Summary</div>
                        <div style={styles.formGroup}>
                          <label style={styles.formLabel}>Subtotal</label>
                          <input
                            type="number"
                            step="0.01"
                            name="subtotal"
                            value={formData.subtotal || '0.00'}
                            readOnly
                            style={{ ...styles.formInput, textAlign: 'right', backgroundColor: '#f8f9fa' }}
                          />
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.formLabel}>Taxes</label>
                          <input
                            type="number"
                            step="0.01"
                            name="taxes"
                            value={formData.taxes || '0.00'}
                            onChange={handleInputChange}
                            style={{ ...styles.formInput, textAlign: 'right' }}
                            onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}
                            onBlur={(e) => Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' })}
                          />
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.formLabel}>Paid</label>
                          <input
                            type="number"
                            step="0.01"
                            name="paid"
                            value={formData.paid || '0.00'}
                            onChange={handleInputChange}
                            style={{ ...styles.formInput, textAlign: 'right' }}
                            onFocus={(e) => Object.assign(e.target.style, styles.formInputFocus)}
                            onBlur={(e) => Object.assign(e.target.style, { borderColor: '#e5e5e5', boxShadow: 'none' })}
                          />
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.formLabel}>Total</label>
                          <input
                            type="number"
                            step="0.01"
                            name="total"
                            value={formData.total || '0.00'}
                            readOnly
                            style={{ ...styles.formInput, textAlign: 'right', backgroundColor: '#f8f9fa' }}
                          />
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.formLabel}>Remaining</label>
                          <input
                            type="number"
                            step="0.01"
                            name="remaining"
                            value={formData.remaining || '0.00'}
                            readOnly
                            style={{ ...styles.formInput, textAlign: 'right', backgroundColor: '#f8f9fa' }}
                          />
                        </div>
                      </div>

                      <div style={styles.buttonGroup}>
                        <Link
                          to={`/invoices/${invoiceNumber}`}
                          style={{ textDecoration: 'none' }}
                        >
                          <button
                            type="button"
                            style={{ ...styles.actionButton, ...styles.secondaryButton }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f3f4f6';
                              e.currentTarget.style.boxShadow = '0 3px 6px rgba(0, 0, 0, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#ffffff';
                              e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                            }}
                          >
                            Cancel
                          </button>
                        </Link>
                        <button
                          type="submit"
                          style={styles.actionButton}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#1e3a8a';
                            e.currentTarget.style.boxShadow = '0 3px 6px rgba(30, 58, 138, 0.25)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#1e40af';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(30, 64, 175, 0.2)';
                          }}
                        >
                          Update Invoice
                        </button>
                      </div>
                    </form>
                  )}

                  <div style={styles.footerSection}>
                    {CLINIC_ADDRESS_FOOTER}
                    <span style={styles.thankYou}>Thank you for your business!</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CSSTransition>
      </TransitionGroup>
    </>
  );
};

export default EditInvoice;